import type { TableColumn, TableRow } from '~/components/ui/table';
import type { Inventory } from '~/graphql/graphql';
import { formatLengthWithBasis, formatQuantityCell } from '~/utils';
import {
  statusFromWeeksCover,
  weeksCoverForInventory,
} from '~/utils/inventory';
import {
  getActionButtons,
  getStockCell,
  getWeeksCovered,
  type TFn,
} from './inventoryCells';

export type FlatRowContext = {
  t: TFn;
  navigate: (path: string) => void;
};

export type InventoryRowOptions = {
  rowIndex: number;
  onOpenEdit: (inventory: Inventory) => void;
  onAddToBuyList: (inventory: Inventory) => void;
};

export function buildFlatColumns(t: TFn): TableColumn[] {
  return [
    {
      key: 'productCode',
      header: t('inventory.product_code'),
      isSortable: true,
    },
    { key: 'product', header: t('inventory.product_name'), isSortable: true },
    { key: 'length', header: t('inventory.length'), isSortable: true },
    { key: 'location', header: t('inventory.location'), isSortable: true },
    {
      key: 'locationCode',
      header: t('inventory.location_code'),
      isSortable: true,
      width: 7,
    },
    {
      key: 'stock',
      header: t('inventory.available_stock'),
      isSortable: true,
      isEditable: true,
      editableType: 'number',
    },
    { key: 'onOrder', header: t('inventory.on_order'), isSortable: true },
    { key: 'requested', header: t('inventory.requested'), isSortable: true },
    { key: 'inbound', header: t('inventory.inbound'), isSortable: true },
    { key: 'weeksCovered', header: t('inventory.cover'), isSortable: true },
    {
      key: 'usageRatePerWeek',
      width: 10,
      header: t('inventory.usage'),
      isSortable: true,
      isEditable: true,
      editableType: 'number',
    },
    { key: 'status', header: t('inventory.status'), isSortable: false },
    {
      key: 'compare',
      header: t('inventory.action'),
      isSortable: false,
      justification: 'end',
      headerJustification: 'start',
      width: 12,
      stickTo: 'right',
    },
  ];
}

export function buildInventoryRow(
  inv: Inventory,
  { t, navigate }: FlatRowContext,
  { rowIndex, onOpenEdit, onAddToBuyList }: InventoryRowOptions
): TableRow {
  const stock = inv.quantity.value;
  const weeksCoveredBeforeUrgent =
    inv.buyerInventoryConfiguration?.weeksCoveredBeforeUrgent ?? 0;
  const weeksCoveredBeforeMonitor =
    inv.buyerInventoryConfiguration?.weeksCoveredBeforeMonitor ?? 0;
  const usageRatePerWeek =
    inv.buyerInventoryConfiguration?.usageRatePerWeek.value ?? 0;
  const weeksCovered = weeksCoverForInventory(inv);
  const productName = inv.platformProductsLength.platformProduct.name;
  const { standardUnitsCode } = inv.platformProductsLength.platformProduct;
  const unit = inv.quantity.unit;
  const usageRatePerWeekUnit =
    inv.buyerInventoryConfiguration?.usageRatePerWeek.unit ?? standardUnitsCode;

  return {
    id: { value: inv.id },
    productCode: {
      value: inv.platformProductsLength?.platformProduct?.code ?? '',
    },
    product: { value: productName ?? '' },
    length: {
      value: inv.platformProductsLength
        ? formatLengthWithBasis(
            inv.platformProductsLength.lengthDisplayValue,
            inv.platformProductsLength.isNominal
          )
        : '',
    },
    locationCode: { value: inv.location?.code ?? '' },
    location: { value: inv.location?.name ?? '' },
    stock: {
      value: getStockCell(inv.availableStock.value, unit),
      editValue: stock,
    },
    onOrder: { value: formatQuantityCell(inv.onOrderStock.value, unit) },
    requested: { value: formatQuantityCell(inv.requestedStock.value, unit) },
    inbound: { value: formatQuantityCell(inv.inboundStock.value, unit) },
    weeksCovered: {
      value: getWeeksCovered(
        weeksCovered,
        weeksCoveredBeforeUrgent,
        weeksCoveredBeforeMonitor
      ),
    },
    usageRatePerWeek: {
      value: (
        <span>
          {usageRatePerWeek} {usageRatePerWeekUnit}/{t('inventory.week')}
        </span>
      ),
    },
    status: {
      value: statusFromWeeksCover(
        weeksCovered,
        weeksCoveredBeforeUrgent,
        weeksCoveredBeforeMonitor,
        t
      ),
    },
    compare: {
      value: getActionButtons(inv, t, {
        testIdSuffix: rowIndex,
        navigate,
        onOpenEdit,
        onAddToBuyList,
      }),
    },
  };
}
